import axios from 'axios';
import { auth, firestore } from '../configs/firebase';
import leedpulse from '../configs/leedpulse';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  OrderByDirection,
  QueryConstraint,
  QuerySnapshot,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import {
  transformToSenderOriginPhoneNumberCode,
} from '@leedpulse-monorepo/utils';
import { ConversationSortType as SortType, JobType, UserType } from '@leedpulse-monorepo/enums';
import { Contact, IUser } from '@leedpulse-monorepo/interfaces';

export const updateContact = async (id: string, data: Contact, apiKey: string) => {
  const contactData: any = data;

  try {
    // Goes through the API and not straight to Firestore: the phone unicity is checked
    // server-side, on the normalized number.
    if (!auth.currentUser) throw new Error('User not authenticated');

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`,
      'x-api-key': apiKey,
    };

    const response = await axios.put(
      `${leedpulse.route_url}/api/v1/contact`,
      { id, ...contactData },
      { headers }
    );

    return {
      status: 'success',
      msg: response.data?.data?.msg || 'Mise à jour du contact réussie.',
    };
  } catch (error: any) {
    return {
      status: 'error',
      msg: error.response?.data?.data?.msg || error.message,
    };
  }
}

export const getContactById = async (id: string) => {
  try {
    const docRef = doc(firestore, 'Contacts', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const oContact = docSnap.data();
      return {
        success: true,
        status: 'success',
        msg: 'Contact récupéré avec succès.',
        doc: oContact,
      };
    } else {
      return {
        success: false,
        status: 'error',
        msg: 'Contact non trouvé',
      };
    }
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toLocaleString(),
    };
  }
}

export const getContactOwnerName = async (ownerId: string) => {
  try {
    const userDocRef = doc(firestore, 'Users', ownerId);
    const userSnapshot = await getDoc(userDocRef);

    if (userSnapshot.exists()) {
      const userData = userSnapshot.data();
      return `${userData.firstname} ${userData.lastname}`;
    } else {
      return 'Inconnu';
    }
  } catch (error) {
    console.error(
      'Erreur lors de la récupération du nom du conseiller attitré:',
      error
    );
    return 'Inconnu';
  }
}

/**
   * Helper function to build filter constraints (used by both getPagedByApiKey and getUpcomingContactsPaged)
   * @private
   */
const buildFilterConstraints = (filter: string | null, scenarioId: string | null, filterUnread: boolean, skipArchivedFilter = false) => {
  const constraints = [];

  // Add filter constraints
  if (filter) {
    if (filter === 'favorite') {
      constraints.push(where('is_favorite', '==', true));
    } else if (filter === 'interested') {
      constraints.push(
        where('qualification_ai.qualification', '==', 'interested')
      );
    } else if (filter === 'replied') {
      constraints.push(where('has_replied', '==', true));
    } else if (filter === 'not_responsive') {
      constraints.push(where('has_replied', '==', false));
      constraints.push(where('last_activity.role', '==', 'assistant'));
    } else if (filter === 'not_interested') {
      constraints.push(
        where('qualification_ai.qualification', '==', 'not_interested')
      );
    } else if (filter === 'considering') {
      constraints.push(
        where('qualification_ai.qualification', '==', 'considering')
      );
    } else if (filter === 'ai_paused') {
      constraints.push(where('messages_settings.ia_pause', '==', true));
    } else if (filter === 'in_progress') {
      const threeDaysAgo = new Date(
        new Date().getTime() - 1000 * 60 * 60 * 24 * 3
      );
      constraints.push(where('last_activity.timestamp', '>', threeDaysAgo));
    } else if (filter === 'with_errors') {
      constraints.push(where('deliverability', '!=', 'delivered'));
      constraints.push(where('deliverability', '!=', 'tbd'));
    }
  }
  
  // Apply archived filter: if skipArchivedFilter is true, don't filter; otherwise exclude archived
  if (!skipArchivedFilter) {
    constraints.push(where('is_archived', '==', false));
  }

  if (scenarioId) {
    constraints.push(where('contact.prospect_category', '==', scenarioId));
  }

  // Add unread only filter
  if (filterUnread) {
    constraints.push(where('messages_counters.frontend.user.unread', '>', 0));
  }

  return constraints;
}

export const getPagedContactsByApiKey = async (
  apiKey: string,
  limitCount: number,
  type: UserType,
  uid: string,
  lastVisible = null,
  filter = null,
  collaboratorId = null,
  scenarioId = null,
  engagedFilter = null,
  filterUnread = false,
  sortOrder: { [key: string]: OrderByDirection } = { [SortType.TIMESTAMP]: 'desc' }
) => {
  try {
    const contactsRef = collection(firestore, 'Contacts');

    // Build base constraints
    const constraints: QueryConstraint[] = [
      where('id_api_key', '==', apiKey),
    ];

    // Add owner constraint: prioritize collaboratorId if provided, otherwise use type-based constraint
    if (collaboratorId) {
      constraints.push(where('contact.owner', '==', collaboratorId));
    } else {
      constraints.push(
        where(
          'contact.owner',
          type === 'user' ? '==' : '!=',
          type === 'user' ? uid : ''
        )
      );
    }

    // Add engaged filter and ordering
    if (engagedFilter === 'engaged') {
      constraints.push(
        where('last_activity.role', 'in', ['user', 'assistant'])
      );
      constraints.push(where('has_replied', '==', true));
      constraints.push(orderBy('last_activity.timestamp', sortOrder[SortType.TIMESTAMP]));
    } else if (engagedFilter === 'upcoming') {
      constraints.push(where('last_activity.role', '==', ''));
      constraints.push(orderBy('last_activity.timestamp', sortOrder[SortType.TIMESTAMP]));
    } else if (engagedFilter === 'not_responsive') {
      constraints.push(where('has_replied', '==', false));
      constraints.push(where('last_activity.role', '==', 'assistant'));
      constraints.push(orderBy('last_activity.timestamp', sortOrder[SortType.TIMESTAMP]));
    } else if (engagedFilter === 'archived') {
      constraints.push(where('is_archived', '==', true));
      constraints.push(orderBy('updated', sortOrder[SortType.TIMESTAMP]));
    } else {
      constraints.push(orderBy('updated', sortOrder[SortType.TIMESTAMP]));
    }

    // Add limit
    constraints.push(limit(limitCount));

    // Add filter constraints using helper function
    // Skip archived filter if we're already filtering by archived tab
    constraints.push(...buildFilterConstraints(filter, scenarioId, filterUnread, engagedFilter === 'archived'));

    // Build query
    let contactsQuery = query(contactsRef, ...constraints);

    // Apply pagination
    if (lastVisible) {
      contactsQuery = query(contactsQuery, startAfter(lastVisible));
    }

    const queryResponse = await getDocs(contactsQuery);

    const aContacts: (Contact & { id_doc: string })[] = [];
    queryResponse.docs.forEach((contact) => {
      const oContact = { ...contact.data() as Contact, id_doc: contact.id };
      oContact.id_doc = contact.id;
      aContacts.push(oContact);
    });

    const lastDocVisible = queryResponse.docs[queryResponse.docs.length - 1];
    return {
      success: true,
      status: 'success',
      msg: `${queryResponse.docs.length} contacts récupérés avec succès.`,
      doc: aContacts,
      hasNextPage: queryResponse.docs.length === limitCount,
      lastVisible: lastDocVisible,
    };
  } catch (error) {
    console.error('Erreur Firestore :', error);
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message ? (error as Error).message.toLocaleString() : 'Erreur inconnue',
    };
  }
}


export const getUpcomingContactsPaged = async (
  apiKey: string,
  limitCount: number,
  type: UserType,
  uid: string,
  lastVisible = null,
  filter = null,
  collaboratorId = null,
  scenarioId = null,
  filterUnread = false
) => {
  try {
    // Get all concern contacts
    const contactsRefTest = collection(firestore, 'Contacts');

    const constraintsTest: QueryConstraint[] = [
      where('id_api_key', '==', apiKey),
      where('last_activity.role', '==', ''),
    ];

    if (collaboratorId) {
      constraintsTest.push(where('contact.owner', '==', collaboratorId));
    } else {
      constraintsTest.push(
        where(
          'contact.owner',
          type === 'user' ? '==' : '!=',
          type === 'user' ? uid : ''
        )
      );
    }

    constraintsTest.push(...buildFilterConstraints(filter, scenarioId, filterUnread));

    const contactsQueryTest = query(contactsRefTest, ...constraintsTest);
    const contactsSnapshotTest = await getDocs(contactsQueryTest);

    if (contactsSnapshotTest.empty) {
      return {
        success: true,
        status: 'success',
        msg: 'Aucun contact trouvé.',
        doc: [],
        hasNextPage: false,
        lastVisible: 0,
      };
    }

    // For each Contact get next job timestamp
    const jobsRefTest = collection(firestore, 'Jobs');

    const nowTest = Date.now();
    const contactsWithJobs = await Promise.all(
      contactsSnapshotTest.docs.map(async (contactDoc) => {
        const contact = contactDoc.data() as Contact;
        const contactId = contact.id || contactDoc.id;

        const jobQuery = query(
          jobsRefTest,
          where('id_api_key', '==', apiKey),
          where('id_contact', '==', contactId),
          where('timestamp', '>', nowTest),
          // A Leedflow contact's workflow jobs are not SMS (index Jobs: id_api_key, id_contact, timestamp, type)
          where('type', '!=', JobType.workflow),
          orderBy('timestamp', 'asc'),
          limit(1)
        );

        const jobSnapshot = await getDocs(jobQuery);
        
        // Determine wich timestamp use
        let finalTimestamp : number;
        if (!jobSnapshot.empty) {
          finalTimestamp  = jobSnapshot.docs[0].data().timestamp;
        } else {
          finalTimestamp  = contact.last_activity?.timestamp || 0;
        }
        return {
              ...contact,
              id_doc: contactDoc.id,
              last_activity: {
                ...contact.last_activity,
                timestamp: finalTimestamp
              },
              nextJobTimestamp: !jobSnapshot.empty ? jobSnapshot.docs[0].data().timestamp : undefined,
        };
      })
    );

    // Sort by timestamp 
    contactsWithJobs.sort((a, b) => a.last_activity.timestamp - b.last_activity.timestamp);
    const upcomingContacts = contactsWithJobs.filter((c) => c.last_activity.timestamp > nowTest);

    // Pagination
    const pageTest = lastVisible ? parseInt(lastVisible) : 0;
    const offsetTest = pageTest * limitCount;
    const paginatedContactsTest = upcomingContacts.slice(offsetTest, offsetTest + limitCount);
    const hasNextPageTest = upcomingContacts.length > offsetTest + limitCount;

    return {
      success: true,
      status: 'success',
      msg: `${paginatedContactsTest.length} contacts récupérés.`,
      doc: paginatedContactsTest,
      hasNextPage: hasNextPageTest,
      lastVisible: pageTest + 1,
    };
  } catch (error) {
    console.error('Erreur Firestore :', error);
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message ? (error as Error).message.toLocaleString() : 'Erreur inconnue',
    };
  }
}

export const getAllContactsByApiKeyWithSearch = async (
  apiKey: string,
  search: string,
  type: UserType,
  uid: string,
  selectedCountry: string | null = null
) => {
  try {
    // Remove spaces with trim()
    search = search.trim().toLowerCase();
    let isPhoneNumberSnapshot;

    // Check if phone number and email is valid
    if (selectedCountry) {
      search = transformToSenderOriginPhoneNumberCode(
        search,
        selectedCountry
      );
    }

    const isNumericSearch = !isNaN(+search) && search.length > 0;

    // Search with Phone Number
    if (isNumericSearch) {
      const contactsRef = collection(firestore, 'Contacts');
      const phoneQuery = query(
        contactsRef,
        where('id_api_key', '==', apiKey),
        where('contact.phone', '==', search),
        where(
          'contact.owner',
          type === 'user' ? '==' : '!=',
          type === 'user' ? uid : ''
        )
      );
      isPhoneNumberSnapshot = await getDocs(phoneQuery) as QuerySnapshot<Contact>;
    }

    let aContactsRaw: QueryDocumentSnapshot<Contact>[] = [];

    if (isPhoneNumberSnapshot)
      aContactsRaw = aContactsRaw.concat(isPhoneNumberSnapshot.docs);

    const aContacts: (Contact & { id_doc: string })[] = [];
    aContactsRaw.forEach((contact) => {
      const oContact = { ...contact.data() as Contact, id_doc: contact.id };
      aContacts.push(oContact);
    });

    return {
      success: true,
      status: 'success',
      msg: 'Liste des contacts récupérée avec succès.',
      doc: aContacts,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toLocaleString(),
    };
  }
}

export const countTotalContactsByApiKey = async (
  apiKey: string,
  type: UserType,
  uid: string,
  filter: string | null = null,
  collaboratorId: string | null = null,
  scenarioId: string | null = null,
  currentTab: string | null = null,
  includeEngagedContacts?: boolean
) => {
  try {
    const contactsCollection = collection(firestore, 'Contacts');

    const constraints: QueryConstraint[] = [
      where('id_api_key', '==', apiKey),
    ];

    // Add owner constraint: prioritize collaboratorId if provided, otherwise use type-based constraint
    if (collaboratorId) {
      constraints.push(where('contact.owner', '==', collaboratorId));
    } else {
      constraints.push(
        where(
          'contact.owner',
          type === 'user' ? '==' : '!=',
          type === 'user' ? uid : ''
        )
      );
    }

    if (currentTab === 'engaged') {
      constraints.push(
        where('last_activity.role', 'in', ['user', 'assistant'])
      );
      constraints.push(where('has_replied', '==', true));
    } else if (currentTab === 'upcoming') {
      constraints.push(where('last_activity.role', '==', ''));
    } else if (currentTab === 'not_responsive') {
      constraints.push(where('has_replied', '==', false));
      constraints.push(
        where('last_activity.role', 'in', ['assistant', 'system'])
      );
    } else if (currentTab === 'archived') {
      constraints.push(where('is_archived', '==', true));
    }

    const isArchivedTab = currentTab === 'archived';
    
    if (filter) {
      if (filter === 'favorite') {
        constraints.push(where('is_favorite', '==', true));
      } else if (filter === 'interested') {
        constraints.push(
          where('qualification_ai.qualification', '==', 'interested')
        );
      } else if (filter === 'replied') {
        constraints.push(where('has_replied', '==', true));
      } else if (filter === 'not_responsive') {
        constraints.push(where('has_replied', '==', false));
        constraints.push(
          where('last_activity.role', 'in', ['assistant', 'system'])
        );
      } else if (filter === 'not_interested') {
        constraints.push(
          where('qualification_ai.qualification', '==', 'not_interested')
        );
      } else if (filter === 'considering') {
        constraints.push(
          where('qualification_ai.qualification', '==', 'considering')
        );
      } else if (filter === 'ai_paused') {
        constraints.push(where('messages_settings.ia_pause', '==', true));
      } else if (filter === 'in_progress') {
        const threeDaysAgo = new Date(
          new Date().getTime() - 1000 * 60 * 60 * 24 * 3
        );
        constraints.push(where('last_activity.timestamp', '>', threeDaysAgo));
      } else if (filter === 'with_errors') {
        constraints.push(where('deliverability', '!=', 'delivered'));
        constraints.push(where('deliverability', '!=', 'tbd'));
      }
    }
    
    if (currentTab && !isArchivedTab) {
      constraints.push(where('is_archived', '==', false));
    }

    if (scenarioId) {
      constraints.push(where('contact.prospect_category', '==', scenarioId));
    }

    // Build query with all constraints
    const contactsQuery = query(contactsCollection, ...constraints);
    const contactsSnapshot = await getCountFromServer(contactsQuery);
    const totalContacts = contactsSnapshot.data().count;
    let engagedContactsCountRes = 0;

    if (includeEngagedContacts) {
      const engagedContactsQuery = query(
        contactsCollection,
        where('id_api_key', '==', apiKey),
        where('is_archived', '==', false),
        where('last_activity.role', 'in', ['user', 'assistant'])
      );

      const engagedContactsSnapshot = await getCountFromServer(
        engagedContactsQuery
      );
      engagedContactsCountRes = engagedContactsSnapshot.data().count;
    }

    return {
      success: true,
      status: 'success',
      msg: `Total ${totalContacts} contacts avec le filtre ${filter || 'aucun'
        } et collaborateur ${collaboratorId || 'tous'}`,
      doc: totalContacts,
      engagedContactsCountRes: engagedContactsCountRes,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: `Erreur lors de la récupération des contacts: ${(error as Error).message}`,
    };
  }
}


export const countTotalContactsByFilter = async (
  apiKey: string,
  type: UserType,
  uid: string,
  filter : string | null = null,
  collaboratorId : string | null = null,
  scenarioId : string | null  = null,
) => {
  try {
    const contactsCollection = collection(firestore, 'Contacts');

    const constraints: QueryConstraint[] = [
      where('id_api_key', '==', apiKey),
    ];

    // Add owner constraint: prioritize collaboratorId if provided, otherwise use type-based constraint
    if (collaboratorId) {
      constraints.push(where('contact.owner', '==', collaboratorId));
    } else {
      constraints.push(
        where(
          'contact.owner',
          type === 'user' ? '==' : '!=',
          type === 'user' ? uid : ''
        )
      );
    }
    
    if (filter) {
      if (filter === 'favorite') {
        constraints.push(where('is_favorite', '==', true));
      } else if (filter === 'interested') {
        constraints.push(
          where('qualification_ai.qualification', '==', 'interested')
        );
      } else if (filter === 'replied') {
        constraints.push(where('has_replied', '==', true));
      } else if (filter === 'not_responsive') {
        constraints.push(where('has_replied', '==', false));
        constraints.push(
          where('last_activity.role', 'in', ['assistant', 'system'])
        );
      } else if (filter === 'not_interested') {
        constraints.push(
          where('qualification_ai.qualification', '==', 'not_interested')
        );
      } else if (filter === 'considering') {
        constraints.push(
          where('qualification_ai.qualification', '==', 'considering')
        );
      } else if (filter === 'ai_paused') {
        constraints.push(where('messages_settings.ia_pause', '==', true));
      } else if (filter === 'in_progress') {
        const threeDaysAgo = new Date(
          new Date().getTime() - 1000 * 60 * 60 * 24 * 3
        );
        constraints.push(where('last_activity.timestamp', '>', threeDaysAgo));
      } else if (filter === 'with_errors') {
        constraints.push(where('deliverability', '!=', 'delivered'));
        constraints.push(where('deliverability', '!=', 'tbd'));
      }
    }
    
    if (scenarioId) {
      constraints.push(where('contact.prospect_category', '==', scenarioId));
    }

    // Build query with all constraints
    const contactsQuery = query(contactsCollection, ...constraints);
    const contactsSnapshot = await getCountFromServer(contactsQuery);
    const totalContacts = contactsSnapshot.data().count;


    return {
      success: true,
      status: 'success',
      msg: `Total ${totalContacts} contacts avec le filtre ${filter || 'aucun'
        } et collaborateur ${collaboratorId || 'tous'}`,
      doc: totalContacts,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: `Erreur lors de la récupération des contacts: ${(error as Error).message}`,
    };
  }
}

export const countAllAndEngagedContacts = async (userData: IUser, apiKey: string) => {
  try {
    const contactsCollection = collection(firestore, 'Contacts');

    const constraints = [where('id_api_key', '==', apiKey)];

    if (userData?.type === UserType.USER) {
      constraints.push(where('contact.owner', '==', userData.uid));
    }

    const allContactsSnapshot = await getCountFromServer(
      query(contactsCollection, ...constraints)
    );
    const totalContacts = allContactsSnapshot.data().count;

    const engagedContactsSnapshot = await getCountFromServer(
      query(
        contactsCollection,
        ...constraints,
        where('last_activity.role', 'in', ['user', 'assistant']),
      )
    );
    const engagedContactsCount = engagedContactsSnapshot.data().count;

    return {
      success: true,
      status: 'success',
      msg: `Total des contacts : ${totalContacts}, contacts engagés : ${engagedContactsCount}.`,
      doc: totalContacts,
      engagedContactsCount: engagedContactsCount,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: `Erreur lors du comptage des contacts : ${(error as Error).message}`,
    };
  }
}

export default {
  updateContact,
  getContactById,
  getContactOwnerName,
  getPagedContactsByApiKey,
  getUpcomingContactsPaged,
  getAllContactsByApiKeyWithSearch,
  countTotalContactsByApiKey,
  countAllAndEngagedContacts,
  countTotalContactsByFilter,
};